import {
  Component,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import {
  DigitsOnlyDirective,
  PhoneOnlyDirective,
} from '../core/directives';
import { EquipmentService } from '../core/services/equipment.service';
import { ExportService } from '../core/services/export.service';
import { InventoryService } from '../core/services/inventory.service';
import { SettingsService } from '../core/services/settings.service';
import { AuthService } from '../modules/auth/auth.service';
import {
  CheckoutRecord,
  EquipmentAsset,
  EquipmentCondition,
  EquipmentStatus,
  Reservation,
} from '../core/models/equipment.models';
import { confirmAction } from '../core/utils/confirm.util';
import {
  defaultBranchFilter,
  isBranchFilterLocked,
  matchesBranchFilter,
} from '../core/utils/branch-scope.util';
import { currentTimestamp, formatDate } from '../core/utils/format.util';
import {
  EQUIPMENT_RENTAL_FLOW,
  reservationStatusLabel,
} from '../core/utils/equipment-rental.util';
import { environment } from '../environments/environment';
import { assertCanTake, parsePositiveInt } from '../core/utils/stock-qty.util';

@Component({
  selector: 'app-check-out',
  standalone: true,
  imports: [FormsModule, DigitsOnlyDirective, PhoneOnlyDirective],
  templateUrl: './check-out.component.html',
  styleUrls: ['./check-out.component.css'],
})
export class CheckOutComponent implements OnInit {
  private readonly equipmentService = inject(EquipmentService);
  private readonly inventory = inject(InventoryService);
  private readonly settings = inject(SettingsService);
  private readonly exporter = inject(ExportService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  readonly stats = this.equipmentService.checkoutPageStats;
  readonly paidReservations =
    this.equipmentService.paidReservationsForIssuePage;
  /** KPIs aligned with button states on this page (includes session issued ids). */
  readonly pageStats = computed(() => {
    const base = this.stats();
    const paidReady = this.paidReservations().filter(
      (row) => this.reservationIssueState(row) === 'ready',
    ).length;
    return {
      ...base,
      awaitingCheckout: paidReady,
      readyTotal: base.available + paidReady,
    };
  });
  readonly readyReservationsCount = computed(
    () =>
      this.paidReservations().filter(
        (row) => this.reservationIssueState(row) === 'ready',
      ).length,
  );
  readonly allAssets = this.equipmentService.allAssets;
  readonly branches = this.settings.scopedBranches;
  readonly reservationStatusLabel = reservationStatusLabel;
  readonly rentalFlowHint = EQUIPMENT_RENTAL_FLOW;
  readonly timestamp = currentTimestamp();
  readonly formatDate = formatDate;
  readonly branchFilterLocked = computed(() => isBranchFilterLocked(this.auth));

  readonly searchQuery = signal('');
  readonly checkedOutSearchQuery = signal('');
  readonly categoryFilter = signal('');
  readonly locationFilter = signal('');
  readonly branchFilter = signal('all');

  readonly categories = computed(() =>
    [
      ...new Set(
        this.equipmentService
          .checkoutDisplayAssets()
          .map((asset) => asset.category),
      ),
    ].sort(),
  );

  readonly locations = computed(() => {
    const fromSettings = this.settings
      .scopedLocations()
      .map((row) => row.name)
      .filter(Boolean);
    const fromAssets = this.equipmentService
      .checkoutDisplayAssets()
      .map((asset) => asset.location)
      .filter(Boolean);
    return [...new Set([...fromSettings, ...fromAssets])].sort();
  });

  readonly filteredAssets = computed(() => {
    const search = this.searchQuery().trim().toLowerCase();
    const category = this.categoryFilter();
    const location = this.locationFilter();

    return this.equipmentService.checkoutDisplayAssets().filter((asset) => {
      if (category && asset.category !== category) {
        return false;
      }
      if (location && asset.location !== location) {
        return false;
      }
      if (
        search &&
        !asset.name.toLowerCase().includes(search) &&
        !asset.assetId.toLowerCase().includes(search) &&
        !asset.category.toLowerCase().includes(search)
      ) {
        return false;
      }
      return true;
    });
  });

  readonly activeCheckedOutCheckouts = computed(() => {
    const search = this.checkedOutSearchQuery().trim().toLowerCase();
    const branch = this.branchFilter();

    return this.equipmentService
      .activeCheckedOutCheckouts()
      .filter((checkout) => {
        if (
          !matchesBranchFilter(
            branch,
            checkout.branch,
            checkout.branchId,
            this.branches(),
          )
        ) {
          return false;
        }
        if (
          search &&
          !checkout.equipment.toLowerCase().includes(search) &&
          !checkout.customer.toLowerCase().includes(search) &&
          !checkout.checkoutNumber.toLowerCase().includes(search) &&
          !(
            checkout.assetNumber &&
            checkout.assetNumber.toLowerCase().includes(search)
          ) &&
          !(
            checkout.receiver &&
            checkout.receiver.toLowerCase().includes(search)
          )
        ) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        const aOverdue = this.equipmentService.isCheckoutOverdue(a);
        const bOverdue = this.equipmentService.isCheckoutOverdue(b);
        if (aOverdue !== bOverdue) {
          return aOverdue ? -1 : 1;
        }
        return (a.dueDate || '').localeCompare(b.dueDate || '');
      });
  });

  /** Equipment choices in the issue modal (walk-in available + current reservation selection). */
  readonly modalAssets = computed(() => {
    const map = new Map<string, EquipmentAsset>();
    for (const asset of this.filteredAssets()) {
      map.set(asset.id, asset);
    }
    const selected = this.selectedAsset();
    if (selected) {
      map.set(selected.id, selected);
    }
    return [...map.values()];
  });

  readonly showCheckoutModal = signal(false);
  readonly showEditModal = signal(false);
  readonly showDetailModal = signal(false);
  readonly selectedAsset = signal<EquipmentAsset | null>(null);
  readonly selectedCheckout = signal<CheckoutRecord | null>(null);
  readonly editingId = signal<string | null>(null);
  readonly editName = signal('');
  readonly editLocation = signal('');
  readonly editCondition = signal<EquipmentCondition>('good');
  readonly checkoutCustomer = signal('');
  readonly checkoutCustomerPhone = signal('');
  readonly checkoutDueDate = signal(this.defaultDueDate());
  readonly checkoutQuantity = signal(1);
  readonly checkoutDestination = signal('');
  readonly checkoutVehicle = signal('');
  /** Store location to take walk-in stock from (required for quantity rentals). */
  readonly checkoutStoreLocationId = signal('');
  readonly activeReservationId = signal<string | null>(null);
  readonly issuingReservationIds = signal<Set<string>>(new Set());
  readonly checkoutSubmitting = signal(false);

  readonly showToast = signal(false);
  readonly toastMessage = signal('');
  readonly toastType = signal<'success' | 'error' | 'info'>('success');

  constructor() {
    effect(() => {
      const branches = this.branches();
      if (!branches.length) {
        return;
      }
      this.branchFilter.set(defaultBranchFilter(this.auth, branches));
    });
  }

  ngOnInit(): void {
    if (!environment.useMockData) {
      this.settings.refreshFromApi();
      this.inventory.loadItemMasterAndStock().subscribe(() => {
        this.equipmentService.refreshEquipmentAssetsFromApi().subscribe();
        this.equipmentService.refreshCheckoutQueueFromApi().subscribe();
      });
    }
  }

  statusCardClass(status: EquipmentStatus): string {
    return status;
  }

  statusBadgeClass(status: EquipmentStatus): string {
    if (status === 'in-repair') {
      return 'in_repair';
    }
    if (status === 'checked-out') {
      return 'checked_out';
    }
    if (status === 'awaiting-checkout') {
      return 'awaiting_checkout';
    }
    return status;
  }

  statusLabel(status: EquipmentStatus): string {
    if (status === 'awaiting-checkout') {
      return 'PAID - AWAITING CHECKOUT';
    }
    return status.replace('-', '_').toUpperCase();
  }

  conditionLabel(condition: EquipmentCondition): string {
    const labels: Record<EquipmentCondition, string> = {
      good: 'Good Condition',
      fair: 'Fair Condition',
      poor: 'Poor Condition',
    };
    return labels[condition];
  }

  isCheckoutDisabled(status: EquipmentStatus): boolean {
    return status !== 'available' && status !== 'awaiting-checkout';
  }

  /** Walk-in quantity rentals can issue more than one unit. */
  readonly isWalkInQuantityCheckout = computed(() => {
    if (this.activeReservationId()) {
      return false;
    }
    const asset = this.selectedAsset();
    if (!asset || asset.pendingCheckoutId || asset.reservationId) {
      return false;
    }
    return this.equipmentService.isQuantityBasedEquipment(asset);
  });

  /** Store locations that still have free stock for the selected walk-in item. */
  readonly walkInStockLocations = computed(() => {
    const asset = this.selectedAsset();
    if (!asset || !this.isWalkInQuantityCheckout()) {
      return [] as Array<{
        id: string;
        name: string;
        branchId?: string;
        available: number;
      }>;
    }

    const inventoryItemID =
      asset.inventoryItemID ??
      (String(asset.id).startsWith('inv-')
        ? Number(String(asset.id).slice(4)) || 0
        : 0);
    if (!inventoryItemID) {
      return [];
    }

    const branchId = asset.branchID
      ? String(asset.branchID)
      : String(this.auth.currentBranchId() || '');

    return this.inventory
      .allStockSnapshots()
      .filter((snap) => {
        if (String(snap.inventoryItemId ?? '') !== String(inventoryItemID)) {
          return false;
        }
        if (branchId && snap.branchId && snap.branchId !== branchId) {
          return false;
        }
        const free = Math.max(0, snap.quantity - (snap.reservedQuantity ?? 0));
        return free > 0 && !!snap.locationId;
      })
      .map((snap) => ({
        id: String(snap.locationId),
        name: snap.location || `Location ${snap.locationId}`,
        branchId: snap.branchId,
        available: Math.max(0, snap.quantity - (snap.reservedQuantity ?? 0)),
      }))
      .sort(
        (a, b) => b.available - a.available || a.name.localeCompare(b.name),
      );
  });

  readonly walkInAvailableQuantity = computed(() => {
    const asset = this.selectedAsset();
    if (!asset) {
      return 0;
    }
    if (!this.isWalkInQuantityCheckout()) {
      return this.availableQuantityForAsset(asset);
    }

    const locationId = this.checkoutStoreLocationId();
    if (!locationId) {
      return 0;
    }

    const inventoryItemID =
      asset.inventoryItemID ??
      (String(asset.id).startsWith('inv-')
        ? Number(String(asset.id).slice(4)) || 0
        : 0);
    if (!inventoryItemID) {
      return 0;
    }

    const branchId = asset.branchID
      ? String(asset.branchID)
      : String(this.auth.currentBranchId() || '');
    const location = this.walkInStockLocations().find(
      (row) => row.id === locationId,
    );
    return this.inventory.availableQuantityAtLocation(
      inventoryItemID,
      branchId || undefined,
      locationId,
      location?.name,
    );
  });

  readonly walkInRemainingAfterCheckout = computed(() => {
    const available = this.walkInAvailableQuantity();
    const qty = this.checkoutQuantity();
    return Math.max(0, available - qty);
  });

  readonly walkInQuantityHint = computed(() => {
    if (!this.isWalkInQuantityCheckout()) {
      return '';
    }
    if (!this.checkoutStoreLocationId()) {
      return 'Select a store location to see available quantity.';
    }
    const available = this.walkInAvailableQuantity();
    const qty = this.checkoutQuantity();
    const location = this.walkInStockLocations().find(
      (row) => row.id === this.checkoutStoreLocationId(),
    );
    const place = location?.name ? ` at ${location.name}` : '';
    if (available <= 0) {
      return `Available Qty: 0${place} — pick another location or receive stock.`;
    }
    if (qty > available) {
      return `Available Qty: ${available}${place}. Reduce quantity — you cannot take more than ${available}.`;
    }
    return `Available Qty: ${available}${place} · issuing ${qty} · ${Math.max(0, available - qty)} left after check-out`;
  });

  availableQuantityForAsset(asset: EquipmentAsset): number {
    return this.equipmentService.walkInFreeQuantity(asset);
  }

  onCheckoutQuantityChange(value: number | string): void {
    const parsed = parsePositiveInt(value, 'Quantity');
    if (!parsed.ok) {
      this.showToastMessage(
        parsed.message ?? 'Enter a valid quantity',
        'error',
      );
      return;
    }
    const max = this.walkInAvailableQuantity();
    if (max > 0 && parsed.value > max) {
      this.checkoutQuantity.set(max);
      this.showToastMessage(
        `Only ${max} available at this location. Quantity set to ${max}.`,
        'info',
      );
      return;
    }
    this.checkoutQuantity.set(parsed.value);
  }

  onCheckoutStoreLocationChange(locationId: string): void {
    this.checkoutStoreLocationId.set(locationId);
    const max = this.walkInAvailableQuantity();
    const qty = this.checkoutQuantity();
    if (max > 0 && qty > max) {
      this.checkoutQuantity.set(max);
    } else if (max <= 0) {
      this.checkoutQuantity.set(1);
    }
  }

  private defaultWalkInStoreLocationId(
    asset: EquipmentAsset | null | undefined,
  ): string {
    if (!asset) {
      return '';
    }
    const inventoryItemID =
      asset.inventoryItemID ??
      (String(asset.id).startsWith('inv-')
        ? Number(String(asset.id).slice(4)) || 0
        : 0);
    if (!inventoryItemID) {
      return '';
    }

    const branchId = asset.branchID
      ? String(asset.branchID)
      : String(this.auth.currentBranchId() || '');

    const stocked = this.inventory
      .allStockSnapshots()
      .filter((snap) => {
        if (String(snap.inventoryItemId ?? '') !== String(inventoryItemID)) {
          return false;
        }
        if (branchId && snap.branchId && snap.branchId !== branchId) {
          return false;
        }
        return (
          Math.max(0, snap.quantity - (snap.reservedQuantity ?? 0)) > 0 &&
          !!snap.locationId
        );
      })
      .sort(
        (a, b) =>
          Math.max(0, b.quantity - (b.reservedQuantity ?? 0)) -
          Math.max(0, a.quantity - (a.reservedQuantity ?? 0)),
      );

    if (asset.locationID) {
      const preferred = String(asset.locationID);
      if (stocked.some((snap) => String(snap.locationId) === preferred)) {
        return preferred;
      }
    }

    if (asset.location?.trim()) {
      const byName = stocked.find(
        (snap) =>
          snap.location.trim().toLowerCase() ===
          asset.location.trim().toLowerCase(),
      );
      if (byName?.locationId) {
        return String(byName.locationId);
      }
    }

    return stocked[0]?.locationId ? String(stocked[0].locationId) : '';
  }

  openCheckoutModal(asset?: EquipmentAsset): void {
    this.selectedAsset.set(asset ?? null);
    this.checkoutCustomer.set(
      asset?.reservedCustomer?.trim() ||
        asset?.checkedOutCustomer?.trim() ||
        '',
    );
    this.checkoutCustomerPhone.set(asset?.checkedOutPhone?.trim() || '');
    this.checkoutDueDate.set(asset?.reservedDueDate || this.defaultDueDate());
    this.checkoutQuantity.set(1);
    this.checkoutDestination.set('');
    this.checkoutVehicle.set('');
    this.checkoutStoreLocationId.set(this.defaultWalkInStoreLocationId(asset));
    this.showCheckoutModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeCheckoutModal(): void {
    const reservationId = this.activeReservationId();
    if (reservationId) {
      this.clearReservationIssuing(reservationId);
    }
    this.showCheckoutModal.set(false);
    this.selectedAsset.set(null);
    this.activeReservationId.set(null);
    this.checkoutStoreLocationId.set('');
    this.checkoutSubmitting.set(false);
    document.body.style.overflow = 'auto';
  }

  closeModalOnBackdrop(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.closeCheckoutModal();
      this.closeEditModal();
      this.closeDetailModal();
    }
  }

  checkoutStatusLabel(status: CheckoutRecord['status']): string {
    if (status === 'overdue') {
      return 'Overdue';
    }
    if (status === 'active') {
      return 'Checked Out';
    }
    return status;
  }

  daysOverdue(checkout: CheckoutRecord): number {
    return this.equipmentService.daysOverdueForCheckout(checkout);
  }

  isOverdue(checkout: CheckoutRecord): boolean {
    return this.equipmentService.isCheckoutOverdue(checkout);
  }

  findAssetForCheckout(checkout: CheckoutRecord): EquipmentAsset | undefined {
    const assetId = checkout.equipmentAssetID
      ? String(checkout.equipmentAssetID)
      : '';
    return (
      this.allAssets().find((row) => row.id === assetId) ??
      this.allAssets().find((row) => row.name === checkout.equipment)
    );
  }

  findAssetIcon(checkout: CheckoutRecord): string {
    return this.findAssetForCheckout(checkout)?.icon ?? 'bi-box-seam';
  }

  openCheckoutDetail(checkout: CheckoutRecord): void {
    this.selectedCheckout.set(checkout);
    this.selectedAsset.set(this.findAssetForCheckout(checkout) ?? null);
    this.showDetailModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeDetailModal(): void {
    this.showDetailModal.set(false);
    this.selectedCheckout.set(null);
    document.body.style.overflow = 'auto';
  }

  goToCheckIn(checkout: CheckoutRecord, focusOverdue = false): void {
    void this.router.navigate(['/check-in'], {
      queryParams: {
        checkout: checkout.id,
        q: checkout.checkoutNumber,
        ...(focusOverdue ? { status: 'overdue' } : {}),
      },
    });
  }

  daysLeftLabel(checkout: CheckoutRecord): {
    label: string;
    className: string;
  } {
    const days = this.daysOverdue(checkout);
    if (days > 0) {
      return { label: `${days}d overdue`, className: 'overdue' };
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(checkout.dueDate);
    due.setHours(0, 0, 0, 0);
    const remaining = Math.ceil(
      (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );
    if (remaining === 0) {
      return { label: 'Due today', className: 'due-today' };
    }
    if (remaining > 0) {
      return { label: `${remaining}d left`, className: 'on-time' };
    }
    return { label: '—', className: '' };
  }

  openEditModal(asset: EquipmentAsset): void {
    if (asset.status !== 'available') {
      return;
    }
    this.editingId.set(asset.id);
    this.editName.set(asset.name);
    this.editLocation.set(asset.location);
    this.editCondition.set(asset.condition);
    this.showEditModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeEditModal(): void {
    this.showEditModal.set(false);
    this.editingId.set(null);
    document.body.style.overflow = 'auto';
  }

  saveAsset(): void {
    const id = this.editingId();
    const name = this.editName().trim();
    const location = this.editLocation().trim();

    if (!id || !name || !location) {
      this.showToastMessage('Please fill in name and location', 'error');
      return;
    }

    this.equipmentService.updateEquipmentAsset(id, {
      name,
      location,
      condition: this.editCondition(),
    });
    this.closeEditModal();
    this.showToastMessage(
      'Item updated locally (no item update API yet)',
      'info',
    );
  }

  deleteAsset(asset: EquipmentAsset): void {
    if (asset.status !== 'available') {
      this.showToastMessage(
        'Cannot delete equipment that is currently checked out.',
        'error',
      );
      return;
    }

    if (
      !confirmAction(
        `Delete item "${asset.name}" (${asset.assetId})? This only removes it from the current session.`,
      )
    ) {
      return;
    }

    if (this.equipmentService.deleteEquipmentAsset(asset.id)) {
      this.showToastMessage('Item removed from the current list', 'info');
    } else {
      this.showToastMessage(
        'Cannot delete equipment that is currently checked out.',
        'error',
      );
    }
  }

  onAssetSelect(assetId: string): void {
    const asset =
      this.modalAssets().find((row) => row.id === assetId) ??
      this.equipmentService.allAssets().find((row) => row.id === assetId) ??
      null;
    this.selectedAsset.set(asset);
    this.checkoutQuantity.set(1);
    this.checkoutStoreLocationId.set(this.defaultWalkInStoreLocationId(asset));
  }

  reservationIssueState(
    reservation: Reservation,
  ): 'ready' | 'issuing' | 'checked-out' {
    if (this.equipmentService.isReservationCheckedOut(reservation)) {
      return 'checked-out';
    }
    if (
      this.issuingReservationIds().has(reservation.id) ||
      (this.checkoutSubmitting() &&
        this.activeReservationId() === reservation.id)
    ) {
      return 'issuing';
    }
    return 'ready';
  }

  private markReservationIssuing(reservationId: string): void {
    this.issuingReservationIds.update((current) => {
      const next = new Set(current);
      next.add(reservationId);
      return next;
    });
  }

  private clearReservationIssuing(reservationId: string): void {
    this.issuingReservationIds.update((current) => {
      const next = new Set(current);
      next.delete(reservationId);
      return next;
    });
  }

  releaseFromReservation(reservation: Reservation): void {
    if (
      this.reservationIssueState(reservation) !== 'ready' ||
      this.checkoutSubmitting()
    ) {
      return;
    }

    this.markReservationIssuing(reservation.id);
    this.activeReservationId.set(reservation.id);
    const equipmentAssetID =
      this.equipmentService.resolveReservationEquipmentAssetId(reservation);
    const inventoryItemID =
      this.equipmentService.resolveReservationInventoryItemId(reservation);
    const pendingCheckoutId =
      reservation.pendingCheckoutId ??
      this.equipmentService.resolvePendingCheckoutForReservation(reservation);

    // Quantity reservations often have inventory + paid-waiting checkout only —
    // no AssetRegister row. Still allow issue via inv-* or pending checkout id.
    const assetId =
      equipmentAssetID > 0
        ? String(equipmentAssetID)
        : inventoryItemID > 0
          ? `inv-${inventoryItemID}`
          : pendingCheckoutId
            ? `res-${reservation.id}`
            : '';

    if (!assetId) {
      this.clearReservationIssuing(reservation.id);
      this.activeReservationId.set(null);
      this.showToastMessage(
        'Could not link this reservation to equipment. Refresh the page, then try again.',
        'error',
      );
      return;
    }

    const asset: EquipmentAsset = (equipmentAssetID > 0
      ? this.equipmentService
          .allAssets()
          .find((row) => row.id === String(equipmentAssetID))
      : undefined) ?? {
      id: assetId,
      assetId:
        equipmentAssetID > 0
          ? `AST-${equipmentAssetID}`
          : reservation.reservationNumber,
      name: reservation.equipment,
      serialNumber: '',
      category: 'Equipment',
      location: reservation.location ?? reservation.branch ?? '',
      status: 'awaiting-checkout',
      condition: 'good',
      reservationId: reservation.id,
      pendingCheckoutId,
      reservedCustomer: reservation.customer,
      reservedDueDate: reservation.endDate,
      inventoryItemID:
        inventoryItemID > 0 ? inventoryItemID : reservation.inventoryItemID,
    };

    this.checkoutCustomer.set(reservation.customer);
    this.checkoutDueDate.set(reservation.endDate || this.defaultDueDate());
    this.openCheckoutModal(asset);
  }

  confirmCheckout(): void {
    if (this.checkoutSubmitting()) {
      return;
    }

    const selected = this.selectedAsset();
    const customer = this.checkoutCustomer().trim();
    const dueDate = this.checkoutDueDate();
    const destination = this.checkoutDestination().trim();
    const reservationId = this.activeReservationId();

    if (!selected || !customer || !dueDate) {
      this.showToastMessage(
        'Please select equipment and enter customer + due date',
        'error',
      );
      return;
    }

    if (!destination) {
      this.showToastMessage(
        'Enter where the equipment is going (destination)',
        'error',
      );
      return;
    }

    const isWalkInPreview =
      !reservationId && !selected.pendingCheckoutId && !selected.reservationId;
    if (
      isWalkInPreview &&
      this.equipmentService.isQuantityBasedEquipment(selected) &&
      !this.checkoutStoreLocationId()
    ) {
      this.showToastMessage(
        'Select a store location to take stock from',
        'error',
      );
      return;
    }

    this.checkoutSubmitting.set(true);
    if (reservationId) {
      this.markReservationIssuing(reservationId);
    }

    const proceed = (asset: EquipmentAsset) => {
      const resolvedId = this.equipmentService.resolveAssetEquipmentId(asset);
      if (resolvedId > 0 && asset.id !== String(resolvedId)) {
        asset = { ...asset, id: String(resolvedId) };
        this.selectedAsset.set(asset);
      }

      const inventoryItemID =
        asset.inventoryItemID ??
        (String(asset.id).startsWith('inv-')
          ? Number(String(asset.id).slice(4)) || 0
          : 0);
      const isWalkIn =
        !reservationId && !asset.pendingCheckoutId && !asset.reservationId;
      const isWalkInQuantity =
        isWalkIn &&
        this.equipmentService.isQuantityBasedEquipment(asset) &&
        inventoryItemID > 0;
      const isReservationIssue =
        !!reservationId || !!asset.reservationId || !!asset.pendingCheckoutId;
      const isReservationQuantityIssue =
        isReservationIssue &&
        (inventoryItemID > 0 || !!asset.pendingCheckoutId);

      if (resolvedId <= 0 && !isWalkInQuantity && !isReservationQuantityIssue) {
        this.checkoutSubmitting.set(false);
        if (reservationId) {
          this.clearReservationIssuing(reservationId);
        }
        this.showToastMessage(
          'Select equipment from the list by name — you should not need to enter an ID manually.',
          'error',
        );
        return;
      }

      if (isWalkIn) {
        if (inventoryItemID > 0) {
          const available = isWalkInQuantity
            ? this.walkInAvailableQuantity()
            : this.availableQuantityForAsset(asset);
          const qtyParsed = parsePositiveInt(
            isWalkInQuantity ? this.checkoutQuantity() : 1,
            'Quantity',
          );
          if (!qtyParsed.ok) {
            this.checkoutSubmitting.set(false);
            this.showToastMessage(
              qtyParsed.message ?? 'Enter a valid quantity',
              'error',
            );
            return;
          }
          if (isWalkInQuantity && !this.checkoutStoreLocationId()) {
            this.checkoutSubmitting.set(false);
            this.showToastMessage(
              'Select a store location to take stock from',
              'error',
            );
            return;
          }
          const check = assertCanTake(available, qtyParsed.value, asset.name);
          if (!check.ok) {
            this.checkoutSubmitting.set(false);
            this.showToastMessage(
              check.message ?? 'This item has no available units.',
              'error',
            );
            return;
          }
        } else if (
          asset.status !== 'available' &&
          asset.status !== 'awaiting-checkout'
        ) {
          this.checkoutSubmitting.set(false);
          this.showToastMessage(
            `${asset.name} is not available to check out.`,
            'error',
          );
          return;
        }
      }

      const linkedReservation = asset.reservationId
        ? this.equipmentService
            .allReservations()
            .find((row) => row.id === asset.reservationId)
        : this.equipmentService
            .allReservations()
            .find(
              (row) =>
                (row.status === 'completed' ||
                  row.status === 'confirmed' ||
                  row.status === 'pending') &&
                (String(row.equipmentAssetID ?? '') === asset.id ||
                  row.reservationNumber === asset.assetId),
            );

      const finishCheckout = (
        result: { success: boolean; message?: string },
        reservationToMark?: string | null,
      ) => {
        this.checkoutSubmitting.set(false);

        if (result.success) {
          if (reservationToMark) {
            this.clearReservationIssuing(reservationToMark);
          }
          this.activeReservationId.set(null);
          const issuedQty = this.checkoutQuantity();
          const remainingQty = this.walkInRemainingAfterCheckout();
          this.closeCheckoutModal();
          const info = /already checked out/i.test(result.message ?? '');
          const qtyNote =
            isWalkInQuantity && !info
              ? ` (${issuedQty} issued, ${remainingQty} left)`
              : '';
          this.showToastMessage(
            (result.message ?? 'Item checked out successfully') + qtyNote,
            info ? 'info' : 'success',
          );
        } else {
          if (reservationToMark) {
            this.clearReservationIssuing(reservationToMark);
          }
          this.activeReservationId.set(null);
          this.showToastMessage(
            result.message ?? 'Failed to check out equipment',
            'error',
          );
        }
      };

      if (isWalkInQuantity) {
        const qty = Math.max(
          1,
          Math.floor(Number(this.checkoutQuantity()) || 1),
        );
        const branchId = asset.branchID
          ? String(asset.branchID)
          : String(this.auth.currentBranchId() || '');
        const storeLocationId = this.checkoutStoreLocationId();
        const locationMeta = this.walkInStockLocations().find(
          (row) => row.id === storeLocationId,
        );
        const locationName =
          locationMeta?.name ||
          this.settings
            .scopedLocations()
            .find((row) => row.id === storeLocationId)?.name;

        if (!storeLocationId) {
          this.checkoutSubmitting.set(false);
          this.showToastMessage(
            'Select a store location to take stock from',
            'error',
          );
          return;
        }

        this.equipmentService
          .checkoutInventoryItemsViaApi({
            branchId,
            storeLocationId,
            locationName,
            customer,
            phone: this.checkoutCustomerPhone().trim(),
            dueDate,
            destination,
            vehicleRegistration: this.checkoutVehicle().trim() || undefined,
            lines: [
              {
                inventoryItemID,
                quantity: qty,
                itemName: asset.name,
              },
            ],
          })
          .subscribe((result) => finishCheckout(result, null));
        return;
      }

      if (asset.pendingCheckoutId || linkedReservation) {
        this.equipmentService
          .checkoutFromReservationViaApi(
            linkedReservation ?? {
              id: asset.reservationId ?? asset.id.replace(/^res-/, ''),
              reservationNumber: asset.checkoutNumber ?? `RES-${asset.id}`,
              customer: asset.reservedCustomer ?? customer,
              equipment: asset.name,
              branch: asset.location,
              location: asset.location,
              startDate:
                asset.checkedOutDate ?? new Date().toISOString().slice(0, 10),
              endDate: asset.reservedDueDate ?? dueDate,
              status: 'completed',
              equipmentAssetID:
                resolvedId > 0
                  ? resolvedId
                  : Number(asset.id) > 0
                    ? Number(asset.id)
                    : undefined,
              inventoryItemID:
                inventoryItemID > 0 ? inventoryItemID : asset.inventoryItemID,
              pendingCheckoutId: asset.pendingCheckoutId,
            },
            customer,
            dueDate,
            {
              phone: this.checkoutCustomerPhone().trim(),
              reservationId:
                reservationId ??
                linkedReservation?.id ??
                asset.reservationId ??
                undefined,
              destination,
              vehicleRegistration: this.checkoutVehicle().trim() || undefined,
            },
          )
          .subscribe((result) =>
            finishCheckout(
              result,
              reservationId ??
                linkedReservation?.id ??
                asset.reservationId ??
                null,
            ),
          );
        return;
      }

      this.equipmentService
        .createCheckoutViaApi(asset.id, customer, dueDate, {
          phone: this.checkoutCustomerPhone().trim(),
          reservationId: reservationId ?? undefined,
          destination,
          vehicleRegistration: this.checkoutVehicle().trim() || undefined,
        })
        .subscribe((result) => finishCheckout(result, reservationId));
    };

    this.inventory.loadItemMasterAndStock().subscribe(() => proceed(selected));
  }

  showToastMessage(
    message: string,
    type: 'success' | 'error' | 'info' = 'success',
  ): void {
    this.toastMessage.set(message);
    this.toastType.set(type);
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  showSuccessToast(message: string): void {
    this.showToastMessage(message, 'success');
  }

  closeToast(): void {
    this.showToast.set(false);
  }

  exportData(): void {
    const rows = this.filteredAssets();
    if (!rows.length) {
      this.showToastMessage(
        'No equipment matches the current filters.',
        'info',
      );
      return;
    }

    this.exporter.downloadCsv(
      `equipment-checkout-${new Date().toISOString().slice(0, 10)}`,
      rows.map((row) => ({
        'Item ID': row.assetId,
        Name: row.name,
        Category: row.category,
        Location: row.location,
        Status: row.status,
        Condition: row.condition,
      })),
    );
  }

  private defaultDueDate(): string {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().slice(0, 10);
  }
}
